import { ICustomizations, createTheme } from '@fluentui/react';
import { IM365ExtendedSemanticColors } from './M365ExtendedSemanticSlots.types';
import { M365TeamsStyles } from './teamsStyles';
import { M365Styles } from '.';

export namespace TeamsLightThemeColorPalette {
  export const themePrimary = '#5b5fc7';
  export const themeLighterAlt = '#f7f7fb';
  export const themeLighter = '#e1e1f1';
  export const themeLight = '#c8c9e4';
  export const themeTertiary = '#989ac9';
  export const themeSecondary = '#7173b0'; // Required by Fabric palette, only used in ShimmerWave
  export const themeDarkAlt = '#585a95';
  export const themeDark = '#4a4c7e';
  export const themeDarker = '#37385d';
  export const neutralLighterAlt = '#eeeeee';
  export const neutralLighter = '#eaeaea';
  export const neutralLight = '#e1e1e1';
  export const neutralQuaternaryAlt = '#d1d1d1';
  export const neutralQuaternary = '#c8c8c8';
  export const neutralTertiaryAlt = '#c0c0c0';
  export const neutralTertiary = '#a19f9d';
  export const neutralSecondary = '#605e5c';
  export const neutralPrimaryAlt = '#3b3a39';
  export const neutralPrimary = '#323130';
  export const neutralDark = '#201f1e';
  export const black = '#000000';
  export const white = '#ffffff';

  // Warnings and errors
  export const errorText = '#990000';
  export const errorBackground = '#FDE7E9';
  export const blockingBackground = '#FDE7E9';
  export const warningBackground = '#FFF4CE';
  export const warningHighlight = '#FFB900';
  export const successBackground = '#DFF6DD';

  // Extended colors

  export const severeWarningBackground = '#FEE6DD';
  export const severeWarningIcon = '#C53601';

  export const dashboardBackdrop = '#DADADA';
  export const overlayBackground = 'rgba(0,0,0,.4)';
  export const navBackground = '#E9E9E9';

  export const dataVis1 = '#0099BC';
  export const dataVis2 = '#77004D';
  export const dataVis3 = '#4F68ED';
  export const dataVis4 = '#AE8C00';
  export const dataVis5 = '#004E8C';
  export const dataVis6 = '#881798';
  export const dataVis7 = '#E43BA6';
  export const dataVis8 = '#0E7878';
  export const dataVis9 = '#8764b8';
  export const dataVis10 = '#814E29';

  export const criticalStatus = '#610000';
  export const highStatus = '#990000';
  export const mediumStatus = '#E60000';
  export const lowStatus = '#F56A00';
  // @deprecated, please use goodStatus instead
  export const readyStatus = '#107C10';
  export const goodStatus = '#107C10';
  export const unkownStatus = '#949494';

  export const needHelpBackground = '#00838C';
  export const needHelpBackgroundPressed = '#006B73';
  // @deprecated - will remain in place but may not be a valid value.
  export const contextualAlertBackground = '#5522E0';

  export const whiteTranslucent40 = 'rgba(255,255,255,.4)';

  export const tagBackgroundLowImpact = '#F3F2F1';
}

// Teams color overrides to ensure proper contrast
const pushButtonText = '#FFFFFF';
const buttonBackgroundOverride = '#E0E0E0';

const p = TeamsLightThemeColorPalette;

const extendedSemanticColors: Partial<IM365ExtendedSemanticColors> = {
  // //////////////////////////////
  // /   Base mapping changes
  // //////////////////////////////
  buttonText: '#242424',
  primaryButtonText: pushButtonText,
  primaryButtonTextPressed: pushButtonText,
  bodyTextChecked: p.neutralDark,
  disabledSubtext: p.neutralQuaternary,
  disabledBodySubtext: p.neutralTertiaryAlt,
  primaryButtonTextDisabled: p.neutralTertiary,
  bodyStandoutBackground: p.neutralLighter,
  buttonBackground: '#FFFFFF',
  buttonBorder: '#D1D1D1',
  buttonBorderDisabled: '#E0E0E0',
  buttonBackgroundHovered: '#FFFFFF',
  buttonBackgroundPressed: buttonBackgroundOverride,
  buttonTextHovered: p.neutralPrimary,
  buttonTextChecked: '#242424',
  buttonBackgroundChecked: buttonBackgroundOverride,
  buttonBackgroundDisabled: '#F0F0F0',
  buttonTextDisabled: '#C7C7C7',
  buttonBackgroundCheckedHovered: buttonBackgroundOverride,
  menuHeader: p.neutralPrimary,
  listItemBackgroundChecked: p.neutralQuaternaryAlt,
  listItemBackgroundCheckedHovered: p.neutralQuaternary,
  link: p.themeDarkAlt,

  // Errors and warnings
  errorText: p.errorText,
  warningHighlight: p.warningHighlight,
  warningText: p.neutralPrimary,

  // Message bar colors
  messageText: p.neutralPrimary,
  messageLink: p.themeDarkAlt,
  messageLinkHovered: p.themeDarker,

  infoIcon: p.neutralSecondary,
  errorIcon: p.highStatus,
  blockingIcon: p.highStatus,
  successIcon: p.goodStatus,
  warningIcon: p.neutralPrimary,
  severeWarningIcon: p.severeWarningIcon,

  infoBackground: p.neutralLighter,
  errorBackground: p.errorBackground,
  blockingBackground: p.blockingBackground,
  warningBackground: p.warningBackground,
  severeWarningBackground: p.severeWarningBackground,
  successBackground: p.successBackground,

  // //////////////////////////////
  // Extended colors
  // //////////////////////////////

  dashboardBackdrop: p.dashboardBackdrop,
  gridItemDestinationBackground: p.neutralQuaternary,
  panelBackground: p.white, // same as bodyBackground in this theme
  overlayBackground: p.overlayBackground,

  // Divider lines
  sectionDividerLine: p.neutralPrimary,
  primaryDividerLine: p.neutralTertiaryAlt,
  secondaryDividerLine: p.neutralQuaternaryAlt,

  // Data vis
  dataVis1: p.dataVis1,
  dataVis2: p.dataVis2,
  dataVis3: p.dataVis3,
  dataVis4: p.dataVis4,
  dataVis5: p.dataVis5,
  dataVis6: p.dataVis6,
  dataVis7: p.dataVis7,
  dataVis8: p.dataVis8,
  dataVis9: p.dataVis9,
  dataVis10: p.dataVis10,

  // Text
  headerText: p.black,
  secondaryListText: p.neutralSecondary,

  // Nav
  navBackground: p.navBackground,
  navItemBackgroundHovered: p.neutralQuaternary,
  navItemBackgroundPressed: p.neutralTertiaryAlt,

  // Status
  criticalStatus: p.criticalStatus,
  highStatus: p.highStatus,
  mediumStatus: p.mediumStatus,
  lowStatus: p.lowStatus,
  readyStatus: p.goodStatus,
  goodStatus: p.goodStatus,
  unkownStatus: p.unkownStatus,

  // Steps
  stepCompleted: p.themePrimary,
  stepCurrent: p.themePrimary,
  stepNotStarted: p.unkownStatus,
  stepModifierBorder: p.themePrimary,
  stepHover: p.themeDark,
  stepPressed: p.themeDarker,
  stepError: p.highStatus,
  allStepsComplete: p.goodStatus,
  /* tslint:disable-next-line:deprecation */
  contextualAlertBackground: p.contextualAlertBackground,

  // Help + Feedback
  needHelpForeground: p.white,
  needHelpBackground: p.needHelpBackground,
  needHelpBackgroundPressed: p.needHelpBackgroundPressed,
  feedbackForeground: p.white,
  feedbackBackground: p.neutralDark,
  feedbackBackgroundPressed: p.neutralSecondary,

  // Because the Fabric controls bank on theme.palette.white/black inverting.
  alwaysWhite: p.white,
  alwaysBlack: p.black,

  // InPageFilter
  filterPillRest: p.neutralLighter,
  filterPillHover: p.neutralLight,
  filterPillPressed: p.neutralQuaternaryAlt,
  filterPillEditedRest: p.themeLighter,
  filterPillEditedHover: p.themeLight,
  filterPillEditedPressed: p.themeTertiary,

  // Tag
  tagBackgroundLowImpact: p.tagBackgroundLowImpact,
  tagBackgroundMediumImpact: p.neutralTertiaryAlt,
  tagBackgroundHighImpact: p.neutralPrimary,
  tagBackgroundNew: p.themePrimary,
  tagBackgroundActiveWarning: p.highStatus,
  tagTextLowImpact: p.neutralPrimary,
  tagTextMediumImpact: p.neutralPrimary,
  tagTextHighImpact: p.white,
  tagTextNew: p.white,
  tagTextActiveWarning: p.white,
};

export const TeamsLightTheme: ICustomizations = {
  settings: {
    theme: createTheme({
      palette: { ...TeamsLightThemeColorPalette },
      semanticColors: extendedSemanticColors,
    }),
  },
  scopedSettings: {},
};

// Re-exporting since the original is actually a customizations object.
export const TeamsLightCustomizations: ICustomizations = TeamsLightTheme;
export const TeamsLightCustomizationsWithStyles: ICustomizations = {
  settings: TeamsLightTheme.settings,
  scopedSettings: { ...M365Styles, ...M365TeamsStyles },
};

/**
 * @deprecated This value is exported solely for test purposes.
 * Please properly instantiate a theme instead of using this value directly.
 */
export const TeamsLightExtendedSemanticColorsForTest = extendedSemanticColors;
